import React, { memo, useState, useEffect, useRef, useCallback } from "react";
import { Box, Button } from "@mui/material";
import * as pdfjsLib from "pdfjs-dist";
import { getPdfjsOptions } from "../../../utils/pdfjsConfig";
import { usePdfRenderer } from "../hooks/usePdfRenderer";
import { usePanzoom } from "../hooks/usePanzoom";
import CustomLoading from "../../../Loading/CustomLoading";
import { preparePdfSource } from "../utils/pdfFetch";

const LOADING_TIMEOUT_MS = 300;

export interface CustomPdfModalProps {
  pdfUrl: string;
  onError?: (error: Error) => void;
  onOrientationChange?: (isLandscape: boolean) => void;
  enablePanzoom?: boolean;
  /**
   * 親要素の高さで埋める (height: 100%)。
   * デフォルトは height: auto + maxHeight: 90vh (フルスクリーンモーダル前提)。
   * split モーダル等で固定高さの枠の中央にローディング/PDF を出したい場合は true。
   */
  fillHeight?: boolean;
}

const CustomPdfModalComponent: React.FC<CustomPdfModalProps> = ({ pdfUrl, onError, onOrientationChange, enablePanzoom = false, fillHeight = false }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLoading, setShowLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pdf, setPdf] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [panzoomElement, setPanzoomElement] = useState<HTMLDivElement | null>(null);

  const panzoomRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setPanzoomElement(node);
    }
  }, []);

  const handleLoadingChange = useCallback((isLoading: boolean) => {
    setLoading(isLoading);
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    if (isLoading) {
      setShowLoading(true);
    } else {
      loadingTimeoutRef.current = setTimeout(() => {
        setShowLoading(false);
        loadingTimeoutRef.current = null;
      }, LOADING_TIMEOUT_MS);
    }
  }, []);

  usePdfRenderer({
    pdf,
    numPages,
    container: panzoomElement,
    scrollContainerRef: containerRef,
    enablePanzoom,
    onLoadingChange: handleLoadingChange,
  });

  usePanzoom({
    container: panzoomElement,
    enabled: enablePanzoom && !loading,
    onLoadingChange: () => {},
  });

  const loadingRef = useRef(false);
  const pdfUrlRef = useRef<string | null>(null);
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!pdfUrl) {
      pdfUrlRef.current = null;
      loadingRef.current = false;
      return;
    }

    if (pdfUrlRef.current === pdfUrl && loadingRef.current) {
      return;
    }

    pdfUrlRef.current = pdfUrl;
    loadingRef.current = true;

    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(false);

        const pdfSource = await preparePdfSource(pdfUrl);

        const loadingTask = pdfjsLib.getDocument(
          typeof pdfSource === "string"
            ? { url: pdfSource, ...getPdfjsOptions() }
            : { data: pdfSource, ...getPdfjsOptions() }
        );

        const loadedPdf = await loadingTask.promise;
        
        if (pdfUrlRef.current !== pdfUrl) {
          loadedPdf.destroy();
          return;
        }
        
        setNumPages(loadedPdf.numPages);
        setPdf(loadedPdf);
        
        if (onOrientationChange && loadedPdf.numPages > 0) {
          try {
            const firstPage = await loadedPdf.getPage(1);
            const viewport = firstPage.getViewport({ scale: 1 });
            const landscape = viewport.width > viewport.height;
            onOrientationChange(landscape);
          } catch (pageError) {
            console.error("ページ取得エラー:", pageError);
          }
        }
      } catch (err) {
        if (pdfUrlRef.current === pdfUrl) {
          console.error("PDF読み込みエラー:", err);
          setLoading(false);
          setError(true);
          onError?.(err as Error);
        }
        loadingRef.current = false;
      }
    };

    loadPdf();
  }, [pdfUrl, onError, onOrientationChange]);

  useEffect(() => {
    return () => {
      if (pdf) {
        pdf.destroy();
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [pdf]);


  useEffect(() => {
    if (containerRef.current) {
      if (loading) {
        containerRef.current.style.overflow = "hidden";
        containerRef.current.style.overflowX = "hidden";
      } else if (enablePanzoom) {
        containerRef.current.style.overflow = "hidden";
      } else {
        containerRef.current.style.overflow = "auto";
        containerRef.current.style.overflowX = "hidden";
      }
    }
  }, [enablePanzoom, loading, numPages]);

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
          color: "#fff",
          padding: "20px",
        }}
      >
        <p>PDFの読み込みに失敗しました</p>
        <Button
          component="a"
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            mt: 2,
            color: "#0b9dbd",
            borderColor: "#0b9dbd",
          }}
          variant="outlined"
        >
          PDFを新しいタブで開く
        </Button>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height: fillHeight ? "100%" : "auto",
        maxHeight: fillHeight ? "100%" : "90vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        position: "relative",
        backgroundColor: "#525252",
        padding: "20px",
        boxSizing: "border-box",
      }}
    >
      <Box
        ref={panzoomRefCallback}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          padding: "20px",
          boxSizing: "border-box",
          visibility: showLoading ? "hidden" : "visible",
          opacity: showLoading ? 0 : 1,
          transition: "opacity 0.2s ease-in-out",
        }}
      />
      {showLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            opacity: loading ? 1 : 0,
            transition: "opacity 0.2s ease-out",
            pointerEvents: loading ? "auto" : "none",
          }}
        >
          <CustomLoading
            loading={showLoading}
            background="transparent"
            fullScreen={false}
          />
        </Box>
      )}
    </Box>
  );
};

CustomPdfModalComponent.displayName = "CustomPdfModal";

const CustomPdfModal = memo(CustomPdfModalComponent);

export default CustomPdfModal;

